import styles from './styles.scss';
import connect from 'components/Shared/connect';
import {
  getProductHeaderInfo,
  getProductBrand,
  getIsNewProduct,
  getIsTopTested,
  getPrice,
  getIsCampaign,
  getTotalNumberOfReviews,
  getAverageReviewScore,
  getEfficiencyArrow,
  getEfficiencyDeclarationUrl,
  getProductId,
  selectTacticalFlagMessage,
  selectTacticalFlagColor,
  getIsPartialCampaign
} from 'views/ProductPage/selector';
import { getProductTagKey, ProductTag, ProductTags } from 'components/Tag';
import { isMobile } from 'components/Shared/responsive';
import { ProductPrice } from './price';
import cn from 'classnames';
import {
  ReviewStats,
  MobileReviewStats,
  DrawerReviewStats
} from 'views/ProductPage/reviews/themes/BGH/reviewStats';
import { splitTitle } from 'components/Shared/Product/selector';
import { REVIEWS_ID } from 'views/ProductPage/reviews';
const reviewsIdLink = `#${REVIEWS_ID}`;
import { EnergyData } from 'components/EnergyData';
import { useAccordionContext } from 'components/Accordion/context';
import { TacticalFlag, TacticalFlagProps } from 'components/TacticalFlag';
import { PriceGuarantee } from './priceGuarantee';
import { RecommendedPrice } from 'views/ProductPage/prices/recommendedPrice';
import { PartPaymentWidget } from '../partPaymentWidget';

interface ProductInfoHeaderProps extends TacticalFlagProps {
  productId: string;
  efficiencyArrow?: string;
  efficiencyDeclarationUrl?: string;
  title: string;
  category?: string;
  variantName: string;
  score: number;
  amountOfReviews: number;
  isTopTested: boolean;
  isNewProduct: boolean;
  isMobile?: boolean;
  tag?: ProductTags;
  handleCopy?: React.ClipboardEventHandler<HTMLHeadingElement>;
}

interface ProductTitleProps {
  title: string;
  variantName: string;
  category?: string;
  mobile?: boolean;
  handleCopy?: React.ClipboardEventHandler<HTMLHeadingElement>;
}
export const ProductTitle = ({
  title,
  variantName,
  category,
  mobile,
  handleCopy
}: ProductTitleProps) => (
  <h1 onCopy={handleCopy} className={mobile ? styles.mobileHeader : styles.header}>
    {category && <div className={styles.category}>{category}</div>}
    <div className={styles.title}>{title}</div>
    <div
      className={cn(styles.review, styles.variant, {
        [styles.mobileReview]: mobile
      })}
    >
      {variantName}
    </div>
  </h1>
);

const DesktopProductInfoHeader = ({
  title,
  category,
  variantName,
  tag,
  productId,
  efficiencyArrow,
  efficiencyDeclarationUrl,
  score,
  amountOfReviews,
  handleCopy,
  tacticalFlagMarking,
  tacticalFlagColor
}: Pick<
  ProductInfoHeaderProps,
  | 'title'
  | 'category'
  | 'variantName'
  | 'score'
  | 'amountOfReviews'
  | 'tag'
  | 'productId'
  | 'efficiencyArrow'
  | 'efficiencyDeclarationUrl'
  | 'handleCopy'
  | 'tacticalFlagMarking'
  | 'tacticalFlagColor'
>) => {
  const hasReviews = Boolean(score) && Boolean(amountOfReviews);

  return (
    <div className={styles.container}>
      <ProductTitle
        title={title}
        category={category}
        variantName={variantName}
        handleCopy={handleCopy}
      />
      <div className={styles.categoryRatingContainer}>
        {hasReviews && (
          <a href={reviewsIdLink}>
            <div className={styles.desktopReviewContainer}>
              <ReviewStats
                starRatingContainerClassName={styles.starContainerRightMargin}
                starRatingClassName={styles.rating}
                amountOfReviewsClassName={styles.review}
              />
            </div>
          </a>
        )}
        <ProductTag type={tag} appearance="large" className={styles.tag} />
        <TacticalFlag
          tacticalFlagMarking={tacticalFlagMarking}
          tacticalFlagColor={tacticalFlagColor}
          className={styles.tag}
          appearance="large"
        />
      </div>
      <RecommendedPrice isProductPage />
      <ProductPrice />
      <EnergyData
        productId={productId}
        efficiencyArrow={efficiencyArrow}
        efficiencyDeclarationUrl={efficiencyDeclarationUrl}
        appearance="large"
        showSavings
      />
      <PriceGuarantee />
      <PartPaymentWidget />
    </div>
  );
};

const isNumber = (candidate: number | undefined): candidate is number =>
  typeof candidate === 'number';

type MobileProductInfoHeaderProps = Partial<
  Pick<ProductInfoHeaderProps, 'amountOfReviews' | 'score'>
> &
  Pick<
    ProductInfoHeaderProps,
    | 'title'
    | 'category'
    | 'variantName'
    | 'tag'
    | 'handleCopy'
    | 'tacticalFlagMarking'
    | 'tacticalFlagColor'
  >;
export const MobileProductInfoHeader = ({
  title,
  category,
  variantName,
  tag,
  score,
  amountOfReviews,
  handleCopy,
  tacticalFlagMarking,
  tacticalFlagColor
}: MobileProductInfoHeaderProps) => {
  const { openReview } = useAccordionContext();

  const reviewStatsComponent =
    isNumber(score) && isNumber(amountOfReviews) ? (
      <DrawerReviewStats score={score} amountOfReviews={amountOfReviews} />
    ) : (
      <MobileReviewStats className={styles.mobileRatingsAlignment} />
    );

  return (
    <div className={styles.mobileCategoryRatingContainer}>
      <div className={styles.mobileReviewSpan}>
        <ProductTitle
          title={title}
          category={category}
          variantName={variantName}
          handleCopy={handleCopy}
          mobile
        />
      </div>

      <div className={cn(styles.mobileRatingContainer, styles.rightAlign)}>
        <a href={reviewsIdLink} onClick={openReview}>
          {reviewStatsComponent}
        </a>
        <ProductTag type={tag} appearance="small" className={styles.tag} />
        <TacticalFlag
          tacticalFlagMarking={tacticalFlagMarking}
          tacticalFlagColor={tacticalFlagColor}
          className={styles.tag}
          appearance="small"
        />
      </div>
    </div>
  );
};

const _ProductInfoHeader = ({
  isMobile,
  ...props
}: Pick<
  ProductInfoHeaderProps,
  | 'title'
  | 'category'
  | 'variantName'
  | 'score'
  | 'amountOfReviews'
  | 'isTopTested'
  | 'isNewProduct'
  | 'isMobile'
  | 'productId'
  | 'efficiencyArrow'
  | 'efficiencyDeclarationUrl'
  | 'handleCopy'
  | 'tacticalFlagMarking'
  | 'tacticalFlagColor'
>) => {
  return isMobile ? (
    <MobileProductInfoHeader {...props} />
  ) : (
    <DesktopProductInfoHeader {...props} />
  );
};

interface StateProps {
  titleSplitPos: number | undefined;
}
export const ProductInfoHeader = connect<
  ProductInfoHeaderProps,
  Pick<ProductInfoHeaderProps, 'handleCopy'>,
  StateProps
>(
  state => ({
    ...getProductHeaderInfo(state.currentPage),
    productId: getProductId(state.currentPage),
    brand: getProductBrand(state.currentPage),
    score: getAverageReviewScore(state),
    amountOfReviews: getTotalNumberOfReviews(state),
    isTopTested: getIsTopTested(state),
    isNewProduct: getIsNewProduct(state.currentPage),
    isCampaign: getIsCampaign(state.currentPage),
    isPartialCampaign: getIsPartialCampaign(state.currentPage),
    price: getPrice(state.currentPage),
    isMobile: isMobile(state),
    efficiencyArrow: getEfficiencyArrow(state),
    efficiencyDeclarationUrl: getEfficiencyDeclarationUrl(state),
    tacticalFlagMarking: selectTacticalFlagMessage(state),
    tacticalFlagColor: selectTacticalFlagColor(state)
  }),
  undefined,
  ({ title, titleSplitPos, isNewProduct, isTopTested, ...s }, d, p) => ({
    ...s,
    ...d,
    ...p,
    ...splitTitle({ title, titleSplitPos }),
    tag: getProductTagKey({ isNewProduct, isTopTested })
  })
)(_ProductInfoHeader);
